import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown, Lock } from "lucide-react";
import { PageHead, Wrap, H2, P, Prose, Impact, Disclaimer } from "@/components/Content";
import { CinematicSection } from "@/components/CinematicSection";
import { ASSETS, API } from "@/lib/api";
import { Seo, breadcrumbSchema } from "@/components/Seo";
import { CASE_STUDIES } from "@/data/caseStudies";

const PermittedGallery = () => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    axios.get(`${API}/gallery`).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);
  if (items === null) return null;

  return (
    <div data-testid="permitted-gallery" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="eyebrow mb-2">Featured With Permission</p>
          <H2 className="!text-xl sm:!text-2xl">DOCUMENTED PHOTO RESULTS.</H2>
        </div>
        <span className="inline-flex items-center gap-2 text-xs font-tech tracking-widest text-[color:var(--chrome-3)]">
          <Lock size={13} className="text-[color:var(--cyan)]" /> SHOWN ONLY WITH CUSTOMER PERMISSION
        </span>
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-[color:var(--line)] glass p-10 text-center">
          <Lock size={26} className="text-[color:var(--cyan)] mx-auto mb-3" />
          <p className="text-[color:var(--chrome-1)] font-semibold">No customer-permitted features yet.</p>
          <p className="text-sm text-[color:var(--chrome-3)] mt-2 max-w-md mx-auto">
            Every submission is private by default. Photo results appear here only when a customer explicitly grants website permission.
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-6">
          {items.map((it) => (
            <article key={it.reference} data-testid={`gallery-item-${it.reference}`} className="rounded-2xl border border-[color:var(--line)] glass p-5">
              <p className="eyebrow mb-3">{it.category}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl overflow-hidden border border-[color:var(--line)] relative">
                  <img src={it.before_url} alt="Before" className="w-full h-40 object-cover" />
                  <span className="absolute bottom-2 left-2 bg-black/70 text-[color:var(--magenta)] text-[10px] font-tech tracking-widest px-2 py-0.5 rounded">BEFORE</span>
                </div>
                <div className="rounded-xl overflow-hidden holo-border relative">
                  <img src={it.after_url} alt="After" className="w-full h-40 object-cover" />
                  <span className="absolute bottom-2 left-2 bg-black/70 text-[color:var(--cyan)] text-[10px] font-tech tracking-widest px-2 py-0.5 rounded">AFTER</span>
                </div>
              </div>
              <p className="text-sm text-[color:var(--chrome-1)] mt-4">{it.label}</p>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default function BeforeAfter() {
  return (
    <div className="bg-ink min-h-screen pb-24">
      <Seo
        title="Before & After Card Care Case Studies | CardScience HQ"
        description="Documented before-and-after case studies from the CardScience HQ lab — what responsible cleaning and preparation can and cannot change. Honest results, no guaranteed grades."
        path="/before-after"
        jsonLd={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Before & After", path: "/before-after" },
        ])}
      />

      <CinematicSection
        image={ASSETS.slabJordan}
        contain
        alt="Before and after card care results"
        label="Before & After"
        headline="WHAT THE PROCESS"
        accent="ACTUALLY CHANGES."
        sentence="Documented, written case studies from the lab — not just photos. Each shows what responsible care addressed, and just as importantly, what it left untouched."
        primaryTo="/submit"
        primaryLabel="SUBMIT A COLLECTIBLE"
        secondaryTo="/results"
        secondaryLabel="Our Results"
      />

      <Wrap className="py-16 space-y-10">
        <Prose>
          <P>
            A before-and-after gallery is easy to fake with lighting and angles. So we document ours in words:
            what arrived, what was responsibly addressed, and what was permanent and left exactly as it was.
          </P>
          <Impact tone="holo">WE DOCUMENT WHAT CHANGED — AND WHAT DIDN'T.</Impact>
        </Prose>

        <PermittedGallery />

        <div className="space-y-8">
          {CASE_STUDIES.map((c) => (
            <article
              key={c.id}
              data-testid={`case-study-${c.id}`}
              className="rounded-2xl border border-[color:var(--line)] glass p-7 sm:p-8"
            >
              <p className="eyebrow mb-2">{c.category}</p>
              <H2 className="!text-xl sm:!text-2xl">{c.title}</H2>

              <div className="grid md:grid-cols-2 gap-6 mt-6">
                <div className="rounded-xl border border-[color:var(--line)] p-5">
                  <p className="font-tech text-[11px] tracking-[0.3em] text-[color:var(--magenta)] mb-3">BEFORE</p>
                  <p className="text-[color:var(--chrome-2)] leading-relaxed text-sm">{c.before}</p>
                </div>
                <div className="rounded-xl holo-border p-5 relative">
                  <ArrowDown size={16} className="md:hidden text-[color:var(--cyan)] absolute -top-3 left-1/2 -translate-x-1/2 bg-ink rounded-full" />
                  <p className="font-tech text-[11px] tracking-[0.3em] text-[color:var(--cyan)] mb-3">AFTER</p>
                  <p className="text-[color:var(--chrome-1)] leading-relaxed text-sm">{c.after}</p>
                </div>
              </div>

              <p className="text-sm text-[color:var(--chrome-3)] leading-relaxed mt-5 pl-4 border-l-2 border-[color:var(--line)]">
                {c.note}
              </p>
            </article>
          ))}
        </div>

        <Disclaimer>
          <p>These case studies describe individual results and do not represent typical outcomes.</p>
          <p>
            No grade is guaranteed. CardScience HQ does not determine grades and is not affiliated with any
            independent grading company. Past results do not guarantee future results.
          </p>
        </Disclaimer>

        <div className="flex flex-wrap gap-3">
          <Link to="/submit" className="btn-holo inline-flex items-center gap-2 rounded-xl px-7 py-4 text-sm">
            SUBMIT A COLLECTIBLE <ArrowRight size={16} />
          </Link>
          <Link to="/results" className="btn-ghost inline-flex items-center gap-2 rounded-xl px-7 py-4 text-sm">
            OUR RESULTS
          </Link>
        </div>
      </Wrap>
    </div>
  );
}
